import { useState } from 'react';
import { RecordPanel } from './RecordPanel';
import type { RecCustomer, RecordsApi } from '@/lib/records';

/**
 * The customer's Sentinel record (Gary, 18 Oct): read first, Edit turns the same fields into a form, Save writes
 * them as Sentinel's customer screen does. Only what an operator changes from a chat: names, numbers, email,
 * address and notes. Rarer fields (VAT, billing terms) stay in Sentinel.
 */
export function CustomerPanel({ api, customer, canEdit, onClose, onSaved, onOpenSentinel }: {
  api: RecordsApi; customer: RecCustomer; canEdit: boolean; onClose: () => void; onSaved: (c: RecCustomer) => void; onOpenSentinel?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [c, setC] = useState(customer);
  const [draft, setDraft] = useState(customer);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof RecCustomer) => (e: { target: { value: string } }) => setDraft({ ...draft, [k]: e.target.value });
  const name = c.type === 'business' && c.company ? c.company : [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || 'Customer';
  const address = [c.street, [c.suburb, c.city].filter(Boolean).join(', '), c.postcode].filter(Boolean);
  const emailOk = !draft.email.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email.trim());
  const stopEditing = () => { setDraft(c); setEditing(false); setError(null); };
  // Edit is a step inside the panel: Back returns to the record, and asks first if something was typed (mobile review, 19 Oct).
  const unsaved = editing && (Object.keys(draft) as (keyof RecCustomer)[]).some((k) => draft[k] !== c[k]);

  async function save() {
    setBusy(true); setError(null);
    try {
      await api.saveCustomer(draft);
      setC(draft); setEditing(false); onSaved(draft);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Not saved. Try again.');
    } finally {
      setBusy(false);
    }
  }

  const footer = editing ? (
    <>
      <button type="button" className="chats-btn chats-btn--ghost" onClick={stopEditing}>Cancel</button>
      <button type="button" className="chats-btn" disabled={busy || !emailOk} onClick={() => void save()}>{busy ? 'Saving…' : 'Save to Sentinel'}</button>
    </>
  ) : (
    <>
      {onOpenSentinel && <button type="button" className="chats-btn chats-btn--ghost" onClick={onOpenSentinel}>Open in Sentinel</button>}
      {canEdit && <button type="button" className="chats-btn" onClick={() => { setDraft(c); setEditing(true); }}>Edit</button>}
    </>
  );

  const since = c.since ? new Date(c.since).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : null;
  return (
    <RecordPanel title={name} sub={[c.tier ? `${c.tier} customer` : 'Customer', since ? `since ${since}` : null, `${c.total} service${c.total === 1 ? '' : 's'}`].filter(Boolean).join(' · ')}
      onClose={onClose} onBack={editing ? stopEditing : undefined} unsaved={unsaved} footer={footer}>
      {error && <p className="chats-rec-error" role="alert">{error}</p>}
      {editing ? (
        <div className="chats-rec-form">
          <div className="chats-rec-two">
            <label>First name<input className="chats-input" value={draft.firstName} onChange={set('firstName')} /></label>
            <label>Last name<input className="chats-input" value={draft.lastName} onChange={set('lastName')} /></label>
          </div>
          <label>Company (if a business)<input className="chats-input" value={draft.company} onChange={set('company')} /></label>
          <div className="chats-rec-two">
            <label>Mobile<input className="chats-input" inputMode="tel" value={draft.mobile} onChange={set('mobile')} /></label>
            <label>Other phone<input className="chats-input" inputMode="tel" value={draft.phone} onChange={set('phone')} /></label>
          </div>
          <label>Email<input className="chats-input" inputMode="email" value={draft.email} onChange={set('email')} />{!emailOk && <small className="chats-rec-field-error">Not a valid email</small>}</label>
          <label>Street<input className="chats-input" value={draft.street} onChange={set('street')} /></label>
          <div className="chats-rec-two">
            <label>Suburb<input className="chats-input" value={draft.suburb} onChange={set('suburb')} /></label>
            <label>Town<input className="chats-input" value={draft.city} onChange={set('city')} /></label>
          </div>
          <label>Postcode<input className="chats-input" inputMode="numeric" value={draft.postcode} onChange={set('postcode')} /></label>
          <label>Notes for the team<textarea className="chats-input" rows={3} value={draft.notes} onChange={set('notes')} /></label>
        </div>
      ) : (
        <>
          <div className="chats-rec-quick">
            {(c.mobile || c.phone) ? <a href={`tel:${(c.mobile || c.phone).replace(/[^\d+]/g, '')}`}>Call</a> : <span className="chats-rec-quick-off">Call</span>}
            {c.email ? <a href={`mailto:${c.email}`}>Email</a> : <span className="chats-rec-quick-off">Email</span>}
            {address.length ? <a href={`https://maps.google.com/?q=${encodeURIComponent(address.join(', '))}`} target="_blank" rel="noopener noreferrer">Map</a> : <span className="chats-rec-quick-off">Map</span>}
          </div>
          <dl className="chats-rec-list">
            <div><dt>Mobile</dt><dd>{c.mobile || 'None'}</dd></div>
            {c.phone && c.phone !== c.mobile && <div><dt>Other phone</dt><dd>{c.phone}</dd></div>}
            <div><dt>Email</dt><dd>{c.email || 'None'}</dd></div>
            <div><dt>Address</dt><dd>{address.length ? address.map((l, i) => <span key={i}>{i > 0 && <br />}{l}</span>) : 'None'}</dd></div>
            <div><dt>Type</dt><dd>{c.type === 'business' ? `Business${c.company ? ` · ${c.company}` : ''}` : 'Residential'}</dd></div>
            <div><dt>Services</dt><dd>{c.last12} in the last 12 months · {c.total} all time</dd></div>
          </dl>
          <p className="chats-rec-label">Notes for the team</p>
          <p className="chats-rec-notes">{c.notes || 'None yet'}</p>
        </>
      )}
    </RecordPanel>
  );
}
